import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Toolbar,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { IoMenuSharp, IoCloseSharp } from "react-icons/io5";
import { FaWhatsappSquare } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

import Auth from '../../Network/auth';

import Logo from "../../assets/images/image1.png";
import Logo2 from "../../assets/images/image2.png";
import Logo3 from "../../assets/images/image3.png";
import Logo4 from "../../assets/images/image4.png";
import Logo5 from "../../assets/images/image5.png";
import Logo6 from "../../assets/images/image6.png";
import PulsatingButton from './components/PulsatingButton';

const content = {
  button: 'Start Now',
  order: 'order-2',
  image: { src: Logo, alt: 'Logo' },
  heading: 'AI Assistant for Teachers and Students',
  paragraph: `Welcome to the future of education with Aishaala, the revolutionary AI voice assistant crafted to elevate the learning experience for both teachers and students.`
};

const content2 = {
  button: 'Get Started',
  order: 'order-1',
  image: { src: Logo4, alt: 'Logo2' },
  heading: 'Intelligent Teaching Support',
  paragraph: `Aishaala offers real-time voice and chat assistance for teachers, simplifying lesson planning and classroom management. 
  With its personalized training and multilingual capabilities, Aishaala customizes lesson plans to fit unique teaching needs and supports various languages, 
  making education more accessible and effective.`
};

const content3 = {
  button: 'Get Started',
  order: 'order-2',
  image: { src: Logo2, alt: 'Logo3' },
  heading: 'Personalized Student Assistance',
  paragraph: `Enhance learning with Aishaala's student-focused features. Our AI provides tailored help and a revision mode that adapts to each student's learning style. 
  Interactive Q&A and writing assistance empower students with instant answers and valuable feedback, making study sessions more productive and engaging.`
};

const content4 = {
  button: 'Get Started',
  order: 'order-1',
  image: { src: Logo3, alt: 'Logo4' },
  heading: 'Effortless Navigation of Knowledge',
  paragraph: `Aishaala streamlines access to a vast array of educational resources. Students can use voice commands to easily navigate through videos, books, notes, and test papers. 
  This capability enables quick location of relevant content and efficient management of diverse study materials.`
};

const content5 = {
  button: 'Get Started',
  order: 'order-2',
  image: { src: Logo5, alt: 'Logo5' },
  heading: 'Customized Knowledge Integration',
  paragraph: `Integrate specialized knowledge seamlessly with Aishaala. Our AI assistant is fine-tuned to align with specific curricula and institutional needs, 
  ensuring that the content delivered is relevant and tailored to the educational focus of your institution.`
};

const content6 = {
  button: 'Get Started',
  order: 'order-1',
  image: { src: Logo6, alt: 'Logo6' },
  heading: 'Engaging Adaptive Testing',
  paragraph: `Revolutionize test preparation with Aishaala's adaptive testing feature. The AI provides dynamic quizzes and practice sessions that adjust in real-time to students' performance. 
  Gamified elements make learning enjoyable while effectively assessing and enhancing student capabilities.`
};

const footerItems = [
  { header: " Aishaala ", subHeaders: [{ title: "About Us", path: "/about" }] },
  { header: "Office", subHeaders: [{ title: "Head Office", path: "", matter: "804, 5th Cross, 4th Block<br/>Koramangala, Bengaluru-560095<br/>contact@aishaala.com" }, { title: "Branch Office", path: "", matter: "293 , Saidulajab , Western Marg , New Delhi - 110030" },] },
  { header: "Quick Links", subHeaders: [{ title: "Blog", path: "/blog" }, { title: "Careers", path: "" }] },
  ,
]
const footerItems2 = [{ header: "Legal Stuff", subHeaders: [{ title: "Privacy Policy", path: "https://publuu.com/flip-book/655483/1461378" }, { title: "Terms of Service", path: "https://publuu.com/flip-book/655483/1461370" }, { title: "Refunds", path: "https://publuu.com/flip-book/655483/1461369" }, { title: "Disclaimer", path: "https://publuu.com/flip-book/655483/1461358" }, { title: "Admin", path: "/admindashboard" },] }]

const testimonials = [
  {
    name: "Birbal Jha",
    designation: "English Trainer",
    testimonial: "Aishaala is a game-changer in education. With its AI voice assistant, the teacher-to-student ratio effectively becomes 1:1, providing a personalized learning experience."
  },
  {
    name: "Indresh Rao",
    designation: "UPSC Faculty",
    testimonial: "Incorporating Aishaala into our UPSC coaching has been a game-changer. The AI assistant's ability to provide personalized guidance and manage vast study materials efficiently has made our preparation process far more organized and effective."
  },
  {
    name: "Samarjeet Tripathi",
    designation: "UPSC Aspirant",
    testimonial: "Navigating the complexities of UPSC preparation was daunting until I started using Aishaala. The AI’s support in managing study resources and delivering prompt feedback has made my study sessions more productive and less stressful."
  },
  {
    name: "Shashank Sharma",
    designation: "District Judge",
    testimonial: "The impact of Aishaala on our legal education has been profound. Its advanced AI features offer seamless access to essential legal knowledge and provide tailored support, significantly enhancing the learning experience for both educators and students."
  },
  {
    name: "Priya Singh",
    designation: "Author, Spiritual Mentor",
    testimonial: "Aishaala has brought a new dimension to my spiritual teachings. The AI’s ability to make lessons interactive and personalized aligns perfectly with my approach, offering deeper insights and a richer experience for my students."
  },
  {
    name: "Devendra Singh Bahadur",
    designation: "Educationist, Technology Leader, MNC",
    testimonial: "As someone deeply involved in education and technology, I’m impressed by how Aishaala integrates seamlessly into the learning process."
  }
];

const NavigationBar = ({ authenticated, handleGoogleLogin }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Toggle hamburger menu
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <AppBar position="static" elevation={0} className="bg-slate-50">
      <Toolbar className="flex justify-between items-center p-4">
        {/* Left Side */}
        <div className="flex items-center space-x-4">
          <h1
            onClick={() => navigate('/')}
            className="font-bold text-xl cursor-pointer text-gray-800"
          >
            Aishaala
          </h1>

          {/* Links for Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/about" className="hover:text-primary text-gray-600">About</Link>
            <Link to="/students" className="hover:text-primary text-gray-600">Students</Link>
            <Link to="/teachers" className="hover:text-primary text-gray-600">Teachers</Link>
            <Link to="/institution" className="hover:text-primary text-gray-600">Institution</Link>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center space-x-4">
          <Link to="/help" className="hover:text-primary text-gray-600 hidden md:block">Help</Link>

          {/* Sign In Button */}
          {!authenticated && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoogleLogin}
              className="bg-primary text-white hover:bg-primary-dark"
            >
              Sign In
            </Button>
          )}
        </div>

        {/* Hamburger Icon (Mobile) */}
        <button
          onClick={toggleMenu}
          className="md:hidden ml-auto text-2xl text-gray-600"
        >
          <IoMenuSharp />
        </button>
      </Toolbar>

      {/* Mobile Menu (Drawer/Popover) */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
          <div className="absolute top-0 right-0 w-3/4 h-full bg-white p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Aishaala</h2>
              <button
                onClick={toggleMenu}
                className="text-2xl text-gray-600"
              >
                <IoCloseSharp />
              </button>
            </div>
            <div className="mt-8">
              <Link to="/about" className="block mb-4 text-gray-600 hover:text-primary" onClick={toggleMenu}>
                About
              </Link>
              <Link to="/students" className="block mb-4 text-gray-600 hover:text-primary" onClick={toggleMenu}>
                Students
              </Link>
              <Link to="/teachers" className="block mb-4 text-gray-600 hover:text-primary" onClick={toggleMenu}>
                Teachers
              </Link>
              <Link to="/institution" className="block mb-4 text-gray-600 hover:text-primary" onClick={toggleMenu}>
                Institution
              </Link>
              <Link to="/help" className="block mb-4 text-gray-600 hover:text-primary" onClick={toggleMenu}>
                Help
              </Link>
            </div>
          </div>
        </div>
      )}
    </AppBar>
  );
};

const BoxComponent = ({ content, onClick, onClicked, authenticated }) => {
  return (
    <div className={twMerge('flex flex-col md:flex-row items-center justify-between p-6', content.order === 'order-1' && 'md:flex-row-reverse')}>
      {/* Image Section */}
      <div className="w-full md:w-1/2 mb-8 md:mb-0">
        <img
          src={content.image.src}
          alt={content.image.alt}
          className="w-full h-auto max-w-xs md:max-w-md mx-auto rounded-lg shadow-lg"
        />
      </div>

      {/* Text Section */}
      <div className="w-full md:w-1/2 md:px-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">{content.heading}</h2>
        <p className="mb-6 text-muted-foreground">{content.paragraph}</p>
        <Button
          variant="contained"
          color="primary"
          onClick={authenticated ? onClick : onClicked}
          className="mt-4"
        >
          {content.button}
        </Button>
      </div>
    </div>
  );
};

const Testimonials = ({ testimonials }) => {
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  return (
    <Box className="overflow-hidden w-full">
      <motion.div
        className="flex"
        animate={{
          x: ['0%', '-100%'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'linear',
        }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <Card
            key={index}
            className="w-[300px] mr-2 flex-shrink-0 bg-gray-100 shadow-none"
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                className="text-xl"
              >
                {testimonial.name}
              </Typography>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
                className="text-sm text-gray-600"
              >
                {testimonial.designation}
              </Typography>
              <Typography
                variant="body1"
                className="text-sm"
              >
                {testimonial.testimonial}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </motion.div>
    </Box>
  );
};

const GetInTouchPopup = ({ isGetInTouchPopupOpen, handleCloseGetInTouchPopup }) => {
  return (
    <Dialog
      open={isGetInTouchPopupOpen}
      onClose={handleCloseGetInTouchPopup}
      maxWidth="sm"
      fullWidth
    >
      {/* Dialog Title with Close Button */}
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span className="text-2xl font-bold">Get in Touch with Us</span>
          <button
            className="text-2xl"
            onClick={handleCloseGetInTouchPopup}
          >
            <IoCloseSharp />
          </button>
        </div>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent>
        <p className="mb-4">We would love to hear from you! Send us a message on WhatsApp.</p>
        <a
          href="https://wa.me/+919972968390?text=Hello, I would like to get in touch regarding Aishaala."
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block"
        >
          <Button
            variant="contained"
            style={{ backgroundColor: '#25D366', color: 'white' }}
            startIcon={<FaWhatsappSquare />}
          >
            WhatsApp
          </Button>
        </a>
      </DialogContent>
    </Dialog>
  );
};

const Footer = ({ footerItems, footerItems2, currentYear }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-gray-100 p-10">
      <div className="footer-items">
        {/* First Set of Footer Items */}
        <div className="flex flex-col md:flex-row px-4 space-y-6 md:space-y-0 md:space-x-6">
          {footerItems.length > 0 &&
            footerItems.map((data, index) => (
              <div key={index} className="footer-col flex-1">
                <h3 className="text-xl font-bold mb-4">{data.header}</h3>
                <div className="footer-col-item">
                  {data.subHeaders.map((dat, inde) => (
                    <div key={inde} className="address mb-2">
                      <p
                        onClick={() => {
                          if (dat.title !== "") {
                            navigate(dat.path);
                          }
                        }}
                        className="cursor-pointer text-gray-600 hover:text-primary"
                      >
                        {dat.title}
                      </p>
                      {dat.matter !== "" && (
                        <p
                          className="text-gray-500"
                          dangerouslySetInnerHTML={{ __html: dat.matter }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

          {footerItems2.length > 0 &&
            footerItems2.map((data, index) => (
              <div key={index} className="footer-col flex-1">
                <h3 className="text-xl font-bold mb-4">{data.header}</h3>
                <div className="footer-col-item">
                  {data.subHeaders.map((dat, inde) => (
                    <p
                      key={inde}
                      onClick={() => window.open(dat.path, '_blank')}
                      className="cursor-pointer text-gray-600 hover:text-primary mb-2"
                    >
                      {dat.title}
                    </p>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Divider */}
      <div className="my-8">
        <hr className="border-gray-300" />
      </div>

      {/* Footer Bottom Section */}
      <div className="footer-bottom text-center">
        <p className="text-gray-600">
          &copy; {currentYear} Aishalaa. All Rights Reserved.
        </p>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { authenticated } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isGetInTouchPopupOpen, setGetInTouchPopupOpen] = useState(false);

  const handleGoogleLogin = async () => {
    try {
      const googleLoginLink = await Auth.getInstance().google();
      window.location.href = googleLoginLink?.url;
    } catch (e) {
      if (e) {
        if (e.response?.status === 401) {
          // eslint-disable-next-line
          console.log('e :: ', e);
          toast.error('Failed to log in with Google');
        }
      }
      throw e;
    }
  };

  const handleViewDemoClick = () => setPopupOpen(true);
  const handleClosePopup = () => setPopupOpen(false);
  const handleGetInTouchClick = () => setGetInTouchPopupOpen(true);
  const handleCloseGetInTouchPopup = () => setGetInTouchPopupOpen(false);
  const handleTryForFreeClick = () => {
    if (authenticated) {
      navigate('/dashboard');
    } else {
      handleGoogleLogin();
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      <NavigationBar
        authenticated={authenticated}
        handleGoogleLogin={handleGoogleLogin}
      />

      <div className="text-center py-16 px-4">
        <h1 className="text-4xl font-bold mb-4">Personalized AI for Teachers & Students</h1>
        <p className="mb-8 max-w-2xl mx-auto">
          Experience the future of education with Aishaala Conversational AI,
          <br />a personalized assistant designed to enhance learning for teachers and Students
        </p>
        <div className="mb-8">
          <div className="inline-block">
            <div className="w-28 h-28">
              <PulsatingButton />
            </div>
          </div>
          <p className="mt-2">Click to Talk</p>
        </div>
        <div className="space-x-4 mb-4">
          <Button
            variant="contained"
            color="primary"
            onClick={handleTryForFreeClick}
          >
            Try For Free
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleGetInTouchClick}
          >
            Get in Touch &gt;
          </Button>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={handleViewDemoClick}
        >
          View Demo
        </Button>
      </div>

      <BoxComponent
        content={content}
        onClick={handleTryForFreeClick}
        onClicked={() => handleGoogleLogin()}
        authenticated={authenticated}
      />
      <BoxComponent
        content={content2}
        onClick={handleTryForFreeClick}
        onClicked={() => handleGoogleLogin()}
        authenticated={authenticated}
      />
      <BoxComponent
        content={content3}
        onClick={handleTryForFreeClick}
        onClicked={() => handleGoogleLogin()}
        authenticated={authenticated}
      />
      <BoxComponent
        content={content4}
        onClick={handleTryForFreeClick}
        onClicked={() => handleGoogleLogin()}
        authenticated={authenticated}
      />
      <BoxComponent
        content={content5}
        onClick={handleTryForFreeClick}
        onClicked={() => handleGoogleLogin()}
        authenticated={authenticated}
      />
      <BoxComponent
        content={content6}
        onClick={handleTryForFreeClick}
        onClicked={() => handleGoogleLogin()}
        authenticated={authenticated}
      />

      <h2 className="text-3xl font-bold text-center my-16">Testimonials</h2>
      <div className="overflow-hidden">
        <Testimonials testimonials={testimonials} />
      </div>

      <footer className="bg-background text-foreground py-8 px-4">
        {/* Add footer content here */}
      </footer>

      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-background p-8 rounded-lg relative max-w-4xl w-full">
            <button
              className="absolute top-2 right-2 text-2xl"
              onClick={handleClosePopup}
            >
              <IoCloseSharp />
            </button>
            <iframe
              className="w-full h-[80vh]"
              src="https://go.screenpal.com/player/cZjhcVV9cF3?width=100%&height=100%&ff=1&title=0"
              allowFullScreen
              title="Demo Video"
            ></iframe>
          </div>
        </div>
      )}

      <GetInTouchPopup
        isGetInTouchPopupOpen={isGetInTouchPopupOpen}
        handleCloseGetInTouchPopup={handleCloseGetInTouchPopup}
      />

      <Footer
        currentYear={new Date().getFullYear()}
        footerItems={footerItems}
        footerItems2={footerItems2}
      />
    </div>
  );
};

export default LandingPage;
